import React, { useState } from 'react'
import { Input, Modal, Spin, Switch } from 'antd'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import { editCustomPayment, editShopPayment, getShopPayment, ICustomPayment, IShopPayment } from '../api'
import { countActivePayments } from '../setting-rules'

const defaultPayments = [
    { id: 0, name: '现金' },
    { id: 1, name: '条码收款' },
    { id: 4, name: '银行卡' },
    { id: 2, name: '个人支付宝' },
    { id: 3, name: '个人微信' },
]

const PaymentSetting: React.FC = () => {
    const [payment, setPayment] = useState<IShopPayment | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [editor, setEditor] = useState<ICustomPayment | 'create' | null>(null)
    const [name, setName] = useState('')

    const load = async () => {
        setLoading(true)
        try {
            setPayment(await getShopPayment())
        } catch (error) {
            $msg('error', error.message || '获取支付方式失败')
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => {
        load()
    }, [])

    const activeDefaults = payment?.paymentconfig?.payment_list || []
    const customPayments = (payment?.custom_payment_config || []).filter(item => item.status !== -1)
    const activeCount = payment ? countActivePayments(payment) : 0

    const save = async (action: () => Promise<void>) => {
        setSaving(true)
        try {
            await action()
            await load()
            $msg('success', '设置成功')

            return true
        } catch (error) {
            $msg('error', error.message || '设置失败')
            await load()

            return false
        } finally {
            setSaving(false)
        }
    }

    const toggle = (id: number, checked: boolean, custom = false) => {
        if (!checked && activeCount <= 1) {
            $msg('error', '至少保留一种支付方式')

            return
        }
        save(() =>
            custom
                ? editCustomPayment({ custom_paymentconfig_id: id, status: checked ? 1 : 0 })
                : editShopPayment(id, checked ? 1 : 0),
        )
    }

    const openEditor = (item: ICustomPayment | 'create') => {
        setEditor(item)
        setName(item === 'create' ? '' : item.name)
    }

    const saveName = async () => {
        const trimmed = name.trim()
        if (!trimmed) {
            $msg('warning', '请输入支付方式名称')

            return
        }
        const params =
            editor === 'create'
                ? { name: trimmed, status: 1 }
                : {
                      custom_paymentconfig_id: (editor as ICustomPayment).id,
                      name: trimmed,
                  }
        if (await save(() => editCustomPayment(params))) {
            setEditor(null)
        }
    }

    const remove = (item: ICustomPayment) => {
        if (!item.can_delete) {
            $msg('warning', '该支付方式已产生历史数据，不允许删除')

            return
        }
        if (item.status === 1 && activeCount <= 1) {
            $msg('warning', '至少保留一种支付方式')

            return
        }
        Modal.confirm({
            title: `删除“${item.name}”？`,
            content: '删除后将无法再使用此支付方式。',
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: () => save(() => editCustomPayment({ custom_paymentconfig_id: item.id, status: -1 })),
        })
    }

    return (
        <div className="cashier-config__payment-setting">
            <Spin spinning={loading}>
                {payment ? (
                    <>
                        <p className="cashier-config__payment-intro">开启的方式会出现在收银台，修改后立即生效。</p>
                        <div className="cashier-config__payment-section-head">
                            <div>
                                <h3>系统默认</h3>
                                <span>常用收款方式</span>
                            </div>
                            <span>
                                {activeDefaults.length}/{defaultPayments.length} 已开启
                            </span>
                        </div>
                        <div className="cashier-config__payment-list">
                            {defaultPayments.map(item => (
                                <div className="cashier-config__payment-item" key={item.id}>
                                    <span className="cashier-config__payment-name">{item.name}</span>
                                    <span className="cashier-config__payment-state">
                                        {activeDefaults.includes(item.id) ? '已开启' : '已关闭'}
                                    </span>
                                    <Switch
                                        aria-label={`${item.name}支付方式`}
                                        checked={activeDefaults.includes(item.id)}
                                        disabled={saving}
                                        onChange={checked => toggle(item.id, checked)}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="cashier-config__payment-section-head cashier-config__payment-section-head--custom">
                            <div>
                                <h3>自定义</h3>
                                <span>用于记录其他收款方式</span>
                            </div>
                            <span>{customPayments.length}/3 已添加</span>
                        </div>
                        <div className="cashier-config__payment-list">
                            {customPayments.map(item => (
                                <div
                                    className="cashier-config__payment-item cashier-config__payment-item--custom"
                                    key={item.id}
                                >
                                    <span className="cashier-config__payment-name">{item.name}</span>
                                    <div className="cashier-config__payment-actions">
                                        <Button types="text" disabled={saving} onClick={() => openEditor(item)}>
                                            编辑
                                        </Button>
                                        <Button types="text" disabled={saving} onClick={() => remove(item)}>
                                            删除
                                        </Button>
                                    </div>
                                    <span className="cashier-config__payment-state">
                                        {item.status === 1 ? '已开启' : '已关闭'}
                                    </span>
                                    <Switch
                                        aria-label={`${item.name}支付方式`}
                                        checked={item.status === 1}
                                        disabled={saving}
                                        onChange={checked => toggle(item.id, checked, true)}
                                    />
                                </div>
                            ))}
                            {!customPayments.length && (
                                <div className="cashier-config__payment-empty">还没有自定义支付方式</div>
                            )}
                        </div>
                        {customPayments.length < 3 && (
                            <div className="cashier-config__payment-add">
                                <Button types="text" disabled={saving} onClick={() => openEditor('create')}>
                                    ＋ 新增支付方式
                                </Button>
                            </div>
                        )}
                    </>
                ) : (
                    <Button types="text" onClick={load}>
                        重新加载支付方式
                    </Button>
                )}
            </Spin>
            <Modal
                title={editor === 'create' ? '新增支付方式' : '编辑支付方式'}
                visible={editor !== null}
                confirmLoading={saving}
                onOk={saveName}
                onCancel={() => setEditor(null)}
                okText="保存"
                destroyOnClose
            >
                <Input
                    value={name}
                    maxLength={10}
                    placeholder="支付方式名称"
                    onChange={event => setName(event.target.value)}
                />
            </Modal>
        </div>
    )
}

export { defaultPayments }
export default PaymentSetting
